import React, { FC, useState } from 'react';
import {
  defaultColors,
  OSmallParagraph,
  OXSmallParagraph,
  CheckCircleIcon,
  IconSize,
  AngleRightIcon,
  Grid,
  GridItem,
} from '@lifechurch/react-ion';
import styled from 'styled-components';
import Link from 'next/link';
import { OnboardingStep } from '@generated/types';
import { useTranslation } from 'react-i18next';

export interface Props {
  onboardingStep: OnboardingStep;
  text: string;
  link: string;
  onSkip: () => void;
}

export enum OnboardingStepKeys {
  SERVICE = 'service',
  CONTENT = 'content',
  CUSTOMIZE = 'customize',
  INVITE = 'invite',
}

interface WrapperProps {
  skipHovered: boolean;
}

const Wrapper = styled.div<WrapperProps>`
  display: inline-block;
  padding: 8px;
  cursor: pointer;
  background-color: white;

  &:hover {
    color: ${props => (props.skipHovered ? defaultColors.gray100 : defaultColors.action.hover)};
    background-color: ${defaultColors.action.light};
  }
`;

const StepTextWrapper = styled.div`
  display: inline-block;
`;

const SkipButton = styled.div`
  text-decoration: underline;
  text-decoration-color: ${defaultColors.gray50};
  cursor: pointer;
  display: inline-block;
  color: ${defaultColors.gray50};

  &:hover {
    color: ${defaultColors.action.hover};
    text-decoration-color: ${defaultColors.action.hover};
  }
`;

const Circle = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 6px;
  border: 1px solid ${defaultColors.gray30};
`;

const ChecklistItem: FC<Props> = ({ link, onboardingStep, onSkip, text }) => {
  const { t } = useTranslation('common');
  const [skipHovered, setSkipHovered] = useState(false);
  const displaySkip =
    (onboardingStep.key === OnboardingStepKeys.CUSTOMIZE ||
      onboardingStep.key === OnboardingStepKeys.INVITE) &&
    !onboardingStep.skipped &&
    !onboardingStep.completed;

  const skipFunctionWrapper = (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    e.stopPropagation();
    onSkip();
  };

  return (
    <Link href={link}>
      <Wrapper skipHovered={skipHovered}>
        <Grid gridTemplateColumns={'18px 150px 20px'}>
          <GridItem gridColumn={'1'} marginBlockStart={'1'}>
            {onboardingStep.completed || onboardingStep.skipped ? (
              <CheckCircleIcon
                color={defaultColors.success.default}
                data-testid='complete-icon'
                marginInlineEnd={2}
                size={IconSize.SM}
              />
            ) : (
              <Circle />
            )}
          </GridItem>
          <GridItem gridColumn={'2'}>
            <StepTextWrapper>
              <OSmallParagraph> {text} </OSmallParagraph>
            </StepTextWrapper>
            {displaySkip && (
              <SkipButton
                onClick={skipFunctionWrapper}
                onMouseEnter={() => setSkipHovered(true)}
                onMouseLeave={() => setSkipHovered(false)}
              >
                <OXSmallParagraph>{t('skip_this_step')}</OXSmallParagraph>
              </SkipButton>
            )}
          </GridItem>
          <GridItem gridColumn={'3'}>
            <AngleRightIcon marginBlockStart={1} size={IconSize.SM} />
          </GridItem>
        </Grid>
      </Wrapper>
    </Link>
  );
};

export default ChecklistItem;
